import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useTeamName } from '../utils'
import { getIdByTeamName, SEASON_SEGMENT_NAMES } from '../../Utils'
import { fetchPlayersWithStatsByTeamGQL, PlayerDataGQL } from '../../Api'
import {
  CURRENT_YEAR,
  hasChampSeriesStarted,
  STATS_YEAR,
} from '../../Config/League/league.config'
import { RosterContextType } from './types'

export const RosterContext = React.createContext<RosterContextType | null>(null)

export const RosterProvider: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const teamName = useTeamName()
  const teamCode = getIdByTeamName(teamName)
  const [isRosterLoading, setIsRosterLoading] = useState(true)
  const [roster, setRoster] = useState<PlayerDataGQL[]>([])
  const [champSeriesRoster, setChampSeriesRoster] = useState<PlayerDataGQL[]>([])

  useEffect(() => {
    if (teamCode) {
      fetchRostersData(teamCode)
    }
  }, [teamCode])

  const fetchRostersData = async (teamId: string) => {
    try {
      const rosters = await fetchPlayersWithStatsByTeamGQL(
        teamId,
        CURRENT_YEAR,
        STATS_YEAR,
        SEASON_SEGMENT_NAMES.regular,
        hasChampSeriesStarted
      )
      if (!rosters) return
      const mainRoster = rosters?.players

      if (rosters?.players && rosters.players.length > 0) {
        setRoster(rosters.players)
      }
      const activeRoster =
        hasChampSeriesStarted && rosters?.champSeries
          ? rosters.champSeries?.roster
          : mainRoster
      if (activeRoster) {
        setChampSeriesRoster(activeRoster)
      }
      setIsRosterLoading(false)
    } catch (e) {
      console.log(e)
      setIsRosterLoading(false)
    }
  }

  const getTeamCaptains = (): PlayerDataGQL[] => {
    return roster.filter((p) => p.isCaptain)
  }

  const contextValue = useMemo(
    () => ({
      teamName,
      roster,
      champSeriesRoster,
      isRosterLoading,
      getTeamCaptains,
    }),
    [teamName, roster, champSeriesRoster, isRosterLoading, getTeamCaptains]
  )

  return <RosterContext.Provider value={contextValue}>{children}</RosterContext.Provider>
}

export const useRosterContext = (): RosterContextType => {
  try {
    const context = useContext(RosterContext)
    if (!context) {
      throw new Error(
        'useRosterContext must be used within a RosterProvider. Make sure your component is wrapped with RosterProvider.'
      )   
    }
    return context
  } catch (error) {
    console.error('Error in useRosterContext:', error)
    throw error
  }
}
